import { uniqBy } from "lodash";
import moment from "moment";
import React, { createElement, cloneElement, useMemo } from "react";
import { WithTooltip } from "@common";
import ClearIcon from "@material-ui/icons/Clear";
import CheckIcon from "@material-ui/icons/Check";
import { makeStyles } from "@material-ui/core";
import { Color } from "@bonliva-crm/ui";
import { QualityApprovalTooltipDataFragment } from "@bonliva-crm/graphql";

type Props = {
  qualityApprovals: QualityApprovalTooltipDataFragment[];
  rootIcon?: React.ReactElement<{ htmlColor: string }>;
  children?: React.ReactElement<any>;
};

export function QualityApprovalTooltip({ children, rootIcon, qualityApprovals }: Props) {
  const classes = useStyles();

  const items = useMemo(
    () =>
      uniqBy(
        qualityApprovals
          .map(({ baseItems, oneOfItems }) => [
            ...(baseItems ?? []),
            ...(oneOfItems ?? [])
          ])
          .flat(),
        "name"
      ),
    [qualityApprovals]
  );

  let isGreen = true;

  for (const item of items) {
    if (!item.approved) {
      isGreen = false;
      break;
    }

    if (item.approvalExpiresOn && new Date(item.approvalExpiresOn) < new Date()) {
      isGreen = false;
      break;
    }
  }

  let content = children;

  if (rootIcon) {
    content = cloneElement(rootIcon, {
      htmlColor: isGreen ? Color.CheckGreen : Color.Error
    });
  }

  return (
    <WithTooltip
      title={
        items.length ? (
          <div className={classes.list}>
            {items.map((item, index) => {
              let icon = ClearIcon;
              let bgColor = Color.Error;

              if (
                item.partiallyExpiresOn &&
                moment(item.partiallyExpiresOn).isBefore(new Date())
              ) {
                icon = CheckIcon;
                bgColor = Color.Yellow;
              } else if (
                item.approvalExpiresOn &&
                moment(item.approvalExpiresOn).isBefore(new Date())
              ) {
                icon = ClearIcon;
                bgColor = Color.Error;
              } else if (item.approved) {
                icon = CheckIcon;
                bgColor = Color.CheckGreen;
              }

              return (
                <div key={index} className={classes.item}>
                  <span className={classes.name}>{item.name}</span>
                  <span className={classes.iconWrap}>
                    <div
                      className={classes.iconWrap}
                      style={{ backgroundColor: bgColor }}
                    >
                      {createElement(icon, {
                        className: classes.icon
                      })}
                    </div>
                  </span>
                </div>
              );
            })}
          </div>
        ) : null
      }
    >
      {content}
    </WithTooltip>
  );
}

const useStyles = makeStyles((theme) => ({
  list: {
    padding: 5,
    minWidth: 180
  },
  item: {
    display: "flex",
    alignItems: "center",
    height: 17
  },
  name: {
    marginRight: 10
  },
  iconWrap: {
    marginLeft: "auto",
    width: 15,
    height: 15,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: "4px"
  },
  icon: {
    width: 13
  }
}));
